import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const QUICK_LINKS = [
  { key: "hero_quick_history", slug: "historical", icon: "ri-ancient-pavilion-line", label: "Tarihi Mekanlar" },
  { key: "hero_quick_food", slug: "food", icon: "ri-restaurant-line", label: "Lezzet" },
  { key: "hero_quick_nature", slug: "nature", icon: "ri-plant-line", label: "Doğa" },
  { key: "hero_quick_hotels", slug: "accommodation", icon: "ri-hotel-line", label: "Konaklama" },
  { key: "hero_quick_fun", slug: "entertainment", icon: "ri-gamepad-line", label: "Eğlence" },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0c91c08aed603ce302b579a7cdcf796a.jpg"
          alt="Bursa panoramic view"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-anthracite-900/70 via-anthracite-900/50 to-anthracite-900/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-anthracite-900/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full pt-24 pb-16 px-4 md:px-8 flex flex-col items-center text-center">
        {/* Title */}
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-4">
          <span className="block">{t("hero_title_1")}</span>
          <span className="block text-forest-400">{t("hero_title_2")}</span>
          <span className="block text-stone-200">{t("hero_title_3")}</span>
        </h1>

        {/* Subtitle */}
        <p className="font-body text-white/70 text-base md:text-xl max-w-2xl leading-relaxed mb-10">
          {t("hero_subtitle")}
        </p>

        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className="w-full max-w-2xl bg-white/12 backdrop-blur-xl border border-white/25 rounded-2xl p-2.5 flex gap-2.5 mb-8"
        >
          <div className="flex-1 flex items-center gap-3 px-5">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-search-line text-white/60 text-xl" />
            </div>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t("hero_search_placeholder")}
              className="flex-1 bg-transparent text-white placeholder:text-white/50 text-base font-body outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-8 py-3.5 bg-forest-600 hover:bg-forest-500 text-white text-sm font-semibold font-body rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("hero_search_btn")}
          </button>
        </form>

        {/* Quick Links — Minimal pill row */}
        <div className="w-full max-w-2xl mb-12">
          <p className="text-white/40 text-xs font-body tracking-widest uppercase mb-4">
            {t("hero_quick_label")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.key}
                to={`/category/${link.slug}`}
                className="group flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 backdrop-blur-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${link.icon} text-white/80 group-hover:text-white text-sm transition-colors`} />
                </div>
                <span className="text-white/80 group-hover:text-white text-sm font-medium font-body transition-colors">
                  {link.label}
                </span>
                <div className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity -mr-1">
                  <i className="ri-arrow-right-line text-white/60 text-xs" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 w-full max-w-3xl">
          {[
            { num: t("hero_stat_1_num"), label: t("hero_stat_1_label") },
            { num: t("hero_stat_2_num"), label: t("hero_stat_2_label") },
            { num: t("hero_stat_3_num"), label: t("hero_stat_3_label") },
            { num: t("hero_stat_4_num"), label: t("hero_stat_4_label") },
          ].map((stat, i) => (
            <div key={i} className="text-center group">
              <div className="font-display text-3xl md:text-4xl font-bold text-white mb-1">
                {stat.num}
              </div>
              <div className="text-white/50 text-xs font-body">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 animate-float">
        <div className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center pt-1">
          <div className="w-1 h-2 rounded-full bg-white/60" />
        </div>
      </div>
    </section>
  );
}
